import { useEffect, useState } from 'react'
import { trackRecordAPI } from '../../services/api'
import type { Phase2DeciderChange } from '../../types'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const pad = (n: number) => String(n).padStart(2, '0')

/** "Oct 20, 2026 00:00 UTC". A zone-less ISO string is read as UTC, as the API writes it. */
export const formatDeciderTime = (iso: string): string | null => {
  const date = new Date(/(Z|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`)
  if (Number.isNaN(date.getTime())) return null
  return `${MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()} ${pad(
    date.getUTCHours()
  )}:${pad(date.getUTCMinutes())} UTC`
}

const WHO: Record<string, string> = {
  model: 'published by the Phase 2 model',
  rule: 'published by the Phase 1 rule',
}

/** The dated who-decided line, or null when the rule has always decided. */
export const deciderNoteText = (changes: Phase2DeciderChange[]): string | null => {
  const parts = changes
    .map((c) => {
      const when = formatDeciderTime(c.at)
      return when && WHO[c.decider] ? `${when} — ${WHO[c.decider]}` : null
    })
    .filter((p): p is string => p !== null)
  return parts.length ? `${parts.join('; ')}.` : null
}

/**
 * Which Phase 2 decider published the engine's signals, as a dated history
 * line on the Track Record (backend services/phase2_decider). Nothing renders
 * while the Phase 1 rule has always decided, or if the history can't load:
 * it is a note beside the record, never one of its figures.
 */
const Phase2DeciderNote = () => {
  const [text, setText] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    Promise.resolve()
      .then(() => trackRecordAPI.getDeciderHistory())
      .then((changes) => live && setText(deciderNoteText(changes ?? [])))
      .catch(() => {})
    return () => {
      live = false
    }
  }, [])

  if (!text) return null
  return (
    <p className="mt-1 text-xs text-gray-400" data-testid="phase2-decider-note">
      {text}
    </p>
  )
}

export default Phase2DeciderNote
